import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Globe, Search, X } from "lucide-react";
import { ModalShell } from "@/components/ui/modal-shell";
import { cn } from "@/lib/utils";
import { setLanguage, SUPPORTED_LANGUAGES, type SupportedLanguage } from "@/i18n";
import { useEnabledLanguages, useIsMultiLanguageEnabled } from "@/i18n/useEnabledLanguages";
import { MARKETS_BY_CODE, countryToMarket, setStoredMarket } from "@/i18n/markets";
import { useCurrency, SUPPORTED_CURRENCIES } from "@/i18n/CurrencyContext";
import { BRAND_GREEN, BRAND_GREEN_SOFT_07 } from "@/lib/design-tokens";
import { useUser } from "@/hooks/useUser";
import { updateUserSettings } from "@/lib/users-api";
import { useLocale } from "@/i18n/LocaleContext";
import { DRIVESHARE_REDIRECT_URL, getDriveShareRedirectUrl } from "@/lib/driveshareRedirect";
import { useGeoHint } from "@/hooks/useCars";
import { useToast } from "@/hooks/use-toast";
import {
  filterCountryPickerCountries,
  getCountryPickerCountries,
  groupCountryPickerCountries,
  LANGUAGE_LABELS,
} from "@/components/layout/country-picker";

const SUPPORTED_LANG_CODES = new Set([...SUPPORTED_LANGUAGES.map((language) => language.code), "en"]);

type Props = {
  iconClassName?: string;
  buttonClassName?: string;
  variant?: "icon" | "labeled";
  align?: "start" | "center" | "end";
};

export function LanguageRegionModal({ iconClassName, buttonClassName, variant = "icon" }: Props) {
  const { t, i18n } = useTranslation();
  const multiEnabled = useIsMultiLanguageEnabled();
  const enabledLanguages = useEnabledLanguages();
  const { currency, setCurrency, currencySaving } = useCurrency();
  const { toast } = useToast();
  const { session } = useUser();
  const { switchLocale, market: urlMarket } = useLocale();
  const { data: geoHint } = useGeoHint();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<"region" | "currency">("region");
  const [countryQuery, setCountryQuery] = useState("");

  // Fire-and-forget persistence of preference changes to the user's server-side
  // settings so picks made from the header survive sign-in on a fresh device.
  // Errors are intentionally swallowed: the local context/i18n change has
  // already taken effect and the next persist attempt will catch up.
  const persistPreference = (updates: { preferred_currency?: string; language?: string }) => {
    const token = session?.access_token;
    if (!token) return;
    updateUserSettings(token, updates).catch(() => { /* noop */ });
  };

  const currentLanguage = i18n.language || "en";
  const currentLang = ["en-US", "en-GB"].includes(currentLanguage) ? "en" : currentLanguage;
  const countryChoices = useMemo(
    () => getCountryPickerCountries(currentLanguage, enabledLanguages.map((language) => language.code)),
    [currentLanguage, enabledLanguages],
  );
  const filteredCountries = useMemo(
    () => filterCountryPickerCountries(countryChoices, countryQuery, currentLanguage),
    [countryChoices, countryQuery, currentLanguage],
  );
  const countryGroups = useMemo(
    () => groupCountryPickerCountries(filteredCountries, currentLanguage),
    [filteredCountries, currentLanguage],
  );
  if (!multiEnabled) return null;

  const visitorMarketCode = countryToMarket(geoHint?.country);
  const visitorCountry = visitorMarketCode
    ? countryChoices.find((country) => country.market.code === visitorMarketCode)
    : undefined;
  const visitorLanguage = visitorCountry?.languages.find(
    (language) => language.code === geoHint?.language?.split(/[-_]/)[0],
  ) || visitorCountry?.languages.find((language) => language.code === visitorCountry.market.defaultLanguage)
    || visitorCountry?.languages[0];
  // Market is now a URL segment (task #865). Fall back to "global" when the
  // user is on bare `/` so the suggested-pick highlight still works.
  const currentMarket = urlMarket || "global";

  const handleRegionPick = async (marketCode: string, langCode: string) => {
    if (!SUPPORTED_LANG_CODES.has(langCode) && langCode !== "en-US" && langCode !== "en-GB") langCode = "en";
    const preferenceCode =
      langCode === "en-US" || langCode === "en-GB" ? langCode :
      langCode === "en" && marketCode === "us" ? "en-US" :
      langCode === "en" && marketCode === "uk" ? "en-GB" :
      langCode;

    // US visitors cross over to driveshare.com — bookaclassic.com never
    // contains /us, so we hard-redirect there with the deep-link helper.
    if (marketCode === "us") {
      setLanguage(preferenceCode as SupportedLanguage);
      const token = session?.access_token;
      if (token) {
        await updateUserSettings(token, { language: preferenceCode }).catch(() => { /* noop */ });
      }
      const target = await getDriveShareRedirectUrl().catch(() => DRIVESHARE_REDIRECT_URL);
      if (typeof window !== "undefined") window.location.assign(target);
      return;
    }

    setStoredMarket(marketCode);
    setLanguage(preferenceCode as SupportedLanguage);
    // Default-language pick → drop /<lang> from the URL so the canonical
    // /<country> form wins (e.g. de+de → /de, not /de/de).
    const marketDefault = MARKETS_BY_CODE[marketCode]?.defaultLanguage;
    const urlLang = langCode.startsWith("en-") ? "en" : langCode;
    const explicitLang = urlLang === marketDefault ? null : urlLang;
    setOpen(false);

    // Persist the language preference fire-and-forget — the navigation that
    // follows is client-side only, so the in-flight PATCH stays alive in the
    // background.
    const token = session?.access_token;
    if (token) {
      void updateUserSettings(token, { language: preferenceCode }).catch(() => { /* noop */ });
    }

    // Client-side navigation through wouter/LocaleContext. The currency
    // context, hreflang tags, featured cars and country-aware copy all
    // re-render reactively because they read from useLocale() now.
    switchLocale({ market: marketCode, language: explicitLang });
  };

  const triggerButton = variant === "labeled" ? (
    <button
      type="button"
      aria-label={t("nav.language")}
      onClick={() => { setCountryQuery(""); setOpen(true); }}
      className={cn("flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-base font-medium text-gray-700", buttonClassName)}
      data-testid="button-language-switcher"
    >
      <Globe className={cn("h-5 w-5", iconClassName)} strokeWidth={1.75} />
      <span>{t("nav.language")}</span>
    </button>
  ) : (
    <button
      type="button"
      aria-label={t("nav.language")}
      onClick={() => { setCountryQuery(""); setOpen(true); }}
      className={cn("flex h-10 w-10 items-center justify-center rounded-full transition-colors", buttonClassName)}
      data-testid="button-language-switcher"
      data-region-anchor=""
    >
      <Globe className={cn("h-5 w-5", iconClassName)} strokeWidth={1.75} />
    </button>
  );

  return (
    <>
      {triggerButton}
      <ModalShell
        open={open}
        onClose={() => { setOpen(false); setCountryQuery(""); }}
        title={t("languageModal.title_aria")}
        titleAlign="center"
        size="3xl"
        padBody={false}
        data-testid="language-region-modal"
        headerExtra={
          <div className="border-b border-gray-200 px-5 sm:px-6">
            <div className="flex gap-6 sm:gap-8">
              {(["region", "currency"] as const).map((key) => {
                const isActive = tab === key;
                const label = key === "region" ? t("languageModal.tab_region") : t("languageModal.tab_currency");
                return (
                  <button
                    key={key}
                    onClick={() => setTab(key)}
                    className={cn(
                      "relative -mb-px py-3.5 text-[14px] font-semibold transition-colors focus:outline-none focus-visible:text-gray-900 sm:text-[15px]",
                      isActive ? "text-gray-900" : "text-gray-500 hover:text-gray-900",
                    )}
                    data-testid={`tab-${key}`}
                  >
                    {label}
                    {isActive && (
                      <span className="absolute inset-x-0 -bottom-px block h-[3px] rounded-full bg-brand" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        }
      >
        {tab === "region" ? (
          <div className="px-5 py-6 sm:px-8 sm:py-7">
            <div className="mb-6 grid gap-3 sm:grid-cols-2">
              {visitorCountry && visitorLanguage && (
                <button
                  type="button"
                  onClick={() => handleRegionPick(visitorCountry.market.code, visitorLanguage.code)}
                  className="rounded-xl border border-gray-200 px-4 py-3 text-left transition-colors hover:border-brand hover:bg-gray-50"
                  data-testid="button-region-visitor-country"
                >
                  <span className="block text-xs font-medium text-gray-500">
                    {t("languageModal.your_location", "Your location")}
                  </span>
                  <span className="mt-1 block text-sm font-semibold text-gray-900">
                    {visitorCountry.market.flag} {visitorCountry.countryName}
                  </span>
                </button>
              )}
              <div className="rounded-xl border border-gray-200 px-4 py-3">
                <span className="block text-xs font-medium text-gray-500">
                  {t("languageModal.worldwide", "Worldwide")}
                </span>
                <div className="mt-1 flex flex-wrap gap-2">
                  {(["en-US", "en-GB"] as const).map((code) => {
                    const countryCode = code === "en-US" ? "us" : "uk";
                    const countryName = countryChoices.find((country) => country.market.code === countryCode)?.countryName
                      || (code === "en-US" ? "United States" : "United Kingdom");
                    const isActive = currentMarket === "global" &&
                      (currentLanguage === "en" ? code === "en-GB" : currentLanguage === code);
                    return (
                      <button
                        key={code}
                        type="button"
                        onClick={() => handleRegionPick("global", code)}
                        className={cn(
                          "rounded-full px-3 py-1.5 text-sm font-medium transition-colors",
                          isActive ? "text-gray-900" : "text-gray-600 hover:bg-gray-100",
                        )}
                        style={isActive ? { backgroundColor: BRAND_GREEN_SOFT_07, boxShadow: `inset 0 0 0 1px ${BRAND_GREEN}` } : undefined}
                        data-testid={`button-region-global-${code}`}
                      >
                        {LANGUAGE_LABELS.en} ({countryName})
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <label htmlFor="language-region-country-search" className="sr-only">
              {t("languageModal.search_country", "Search countries")}
            </label>
            <div className="relative mb-5">
              <Search aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                id="language-region-country-search"
                type="search"
                value={countryQuery}
                onChange={(event) => setCountryQuery(event.target.value)}
                placeholder={t("languageModal.search_country", "Search countries")}
                className="h-11 w-full rounded-xl border border-gray-300 bg-white pl-10 pr-10 text-sm text-gray-900 outline-none transition focus:border-brand focus:ring-2 focus:ring-brand/20"
                autoComplete="off"
                data-testid="input-country-search"
              />
              {countryQuery && (
                <button
                  type="button"
                  aria-label={t("common.clear", "Clear")}
                  onClick={() => setCountryQuery("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-2 text-gray-500 hover:bg-gray-100"
                  data-testid="button-clear-country-search"
                >
                  <X aria-hidden="true" className="h-4 w-4" />
                </button>
              )}
            </div>

            <h3 className="mb-4 text-[18px] font-bold text-gray-900">
              {t("languageModal.countries", "Countries")}
            </h3>
            {countryGroups.length === 0 ? (
              <p className="py-4 text-sm text-gray-500" role="status">
                {t("languageModal.no_countries_found", "No countries found")}
              </p>
            ) : (
              <div
                className="columns-1 gap-x-8 sm:columns-2 lg:columns-3"
                data-testid="country-picker-groups"
              >
                {countryGroups.map((group) => (
                  <section key={group.letter} className="mb-5 break-inside-avoid">
                    <h4 className="mb-2 border-b border-gray-200 pb-1 text-xs font-bold uppercase tracking-wider text-gray-500">
                      {group.letter}
                    </h4>
                    <ul className="space-y-1">
                      {group.countries.map((country) => (
                        <li key={country.market.code}>
                          <div className="rounded-lg px-2 py-2 hover:bg-gray-50">
                            <p className="text-sm font-semibold leading-tight text-gray-900">
                              <span aria-hidden="true" className="mr-1.5">{country.market.flag}</span>
                              {country.countryName}
                            </p>
                            <div className="mt-1 flex flex-wrap gap-1.5">
                              {country.languages.map((language) => {
                                const isActive = country.market.code === currentMarket &&
                                  (language.code === "en" ? currentLang === "en" : language.code === currentLang);
                                return (
                                  <button
                                    key={language.code}
                                    type="button"
                                    onClick={() => handleRegionPick(country.market.code, language.code)}
                                    className={cn(
                                      "rounded-full px-2.5 py-1 text-xs font-medium transition-colors",
                                      isActive ? "text-gray-900" : "text-gray-600 hover:bg-gray-200",
                                    )}
                                    style={isActive ? { backgroundColor: BRAND_GREEN_SOFT_07, boxShadow: `inset 0 0 0 1px ${BRAND_GREEN}` } : undefined}
                                    data-testid={`button-region-${country.market.code}-${language.code}`}
                                    aria-pressed={isActive}
                                  >
                                    {language.label}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </section>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="px-5 py-6 sm:px-8 sm:py-7">
            <h3 className="mb-3 text-[18px] font-bold text-gray-900">
              {t("languageModal.choose_currency")}
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-x-4 gap-y-1">
              {SUPPORTED_CURRENCIES.map((c) => {
                const active = c.code === currency;
                return (
                  <button
                    key={c.code}
                    disabled={currencySaving}
                    onClick={async () => {
                      if (await setCurrency(c.code)) setOpen(false);
                      else toast({ title: t("common.error"), variant: "destructive" });
                    }}
                    className={cn(
                      "block rounded-lg px-3 py-2 text-left transition-colors focus:outline-none",
                      active ? "" : "hover:bg-gray-50",
                    )}
                    style={
                      active
                        ? { backgroundColor: BRAND_GREEN_SOFT_07, boxShadow: `inset 0 0 0 2px ${BRAND_GREEN}` }
                        : undefined
                    }
                    data-testid={`button-currency-${c.code}`}
                  >
                    <p className="text-[14px] font-medium text-gray-900 leading-tight">{c.label}</p>
                    <p className="text-[13px] text-gray-500 leading-tight mt-0.5">{c.code} · {c.symbol}</p>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </ModalShell>
    </>
  );
}
